import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Navigation prefetch configuration
 * Maps routes to their likely next navigation targets
 */
const PREFETCH_ROUTES: Record<string, string[]> = {
  '/dashboard': ['/equipment', '/inspections', '/maintenance'],
  '/equipment': ['/inspections', '/maintenance'],
  '/inspections': ['/equipment', '/inspections/checklists'],
  '/inspections/checklists': ['/inspections'],
  '/maintenance': ['/equipment', '/inventory'],
  '/inventory': ['/maintenance'],
  '/users': ['/departments', '/tenants'],
  '/departments': ['/users'],
  '/tenants': ['/users', '/departments'],
};

/**
 * Query key generators for different data types
 * These should match the query keys used in your API calls
 */
export const queryKeys = {
  equipment: (params?: Record<string, unknown>) => ['equipment', params],
  inspections: (params?: Record<string, unknown>) => ['inspections', params],
  maintenance: (params?: Record<string, unknown>) => ['maintenance', params],
  inventory: (params?: Record<string, unknown>) => ['inventory', params],
  users: (params?: Record<string, unknown>) => ['users', params],
  departments: (params?: Record<string, unknown>) => ['departments', params],
  tenants: (params?: Record<string, unknown>) => ['tenants', params],
  checklists: (params?: Record<string, unknown>) => ['checklists', params],
};

/**
 * Stale time configuration for different data types
 * Determines how long cached data is considered fresh
 */
export const staleTimeConfig = {
  // Frequently changing data
  dashboard: 30 * 1000, // 30 seconds
  inspections: 60 * 1000, // 1 minute
  maintenance: 60 * 1000, // 1 minute
  
  // Moderately changing data
  equipment: 5 * 60 * 1000, // 5 minutes
  inventory: 5 * 60 * 1000, // 5 minutes
  
  // Rarely changing data
  users: 10 * 60 * 1000, // 10 minutes
  departments: 15 * 60 * 1000, // 15 minutes
  tenants: 15 * 60 * 1000, // 15 minutes
  checklists: 15 * 60 * 1000, // 15 minutes
  
  // Static/reference data
  equipmentTypes: 30 * 60 * 1000, // 30 minutes
  roles: 30 * 60 * 1000, // 30 minutes
  permissions: 30 * 60 * 1000, // 30 minutes
};

/**
 * Hook to prefetch data for likely next navigation targets
 * 
 * Automatically prefetches data when hovering over navigation links
 * or when on a page with known next destinations.
 * 
 * **Validates: Requirements 9.2, 9.5**
 * 
 * @example
 * ```tsx
 * const MyPage = () => {
 *   usePrefetch();
 *   
 *   return <div>Page content</div>;
 * };
 * ```
 */
export const usePrefetch = () => {
  const queryClient = useQueryClient();
  const location = useLocation();

  useEffect(() => {
    const currentPath = location.pathname;
    const nextRoutes = PREFETCH_ROUTES[currentPath];

    if (nextRoutes) {
      // Prefetch data for likely next routes
      nextRoutes.forEach((route) => {
        prefetchRouteData(route, queryClient);
      });
    }
  }, [location.pathname, queryClient]);
};

/**
 * Prefetch data for a specific route
 */
const prefetchRouteData = async (
  route: string,
  queryClient: ReturnType<typeof useQueryClient>
) => {
  // Map routes to their data requirements
  const routeDataMap: Record<string, () => void> = {
    '/equipment': () => {
      // Prefetch equipment list with default params
      queryClient.prefetchQuery({
        queryKey: queryKeys.equipment({ page: 1, pageSize: 25 }),
        queryFn: () => import('../services/api').then(m => 
          m.apiService.fetchEquipment({ page: 1, pageSize: 25 })
        ),
        staleTime: staleTimeConfig.equipment,
      });
    },
    '/inspections': () => {
      queryClient.prefetchQuery({
        queryKey: queryKeys.inspections({ page: 1, pageSize: 25 }),
        queryFn: () => import('../services/api').then(m => 
          m.apiService.fetchInspections({ page: 1, pageSize: 25 })
        ),
        staleTime: staleTimeConfig.inspections,
      });
    },
    '/maintenance': () => {
      queryClient.prefetchQuery({
        queryKey: queryKeys.maintenance({ page: 1, pageSize: 25 }),
        queryFn: () => import('../services/api').then(m => 
          m.apiService.fetchMaintenanceTickets({ page: 1, pageSize: 25 })
        ),
        staleTime: staleTimeConfig.maintenance,
      });
    },
    '/inventory': () => {
      queryClient.prefetchQuery({
        queryKey: queryKeys.inventory({ page: 1, pageSize: 25 }),
        queryFn: () => import('../services/api').then(m => 
          m.apiService.fetchInventory({ page: 1, pageSize: 25 })
        ),
        staleTime: staleTimeConfig.inventory,
      });
    },
    '/users': () => {
      queryClient.prefetchQuery({
        queryKey: queryKeys.users({ page: 1, pageSize: 25 }),
        queryFn: () => import('../services/api').then(m => 
          m.apiService.fetchUsers({ page: 1, pageSize: 25 })
        ),
        staleTime: staleTimeConfig.users,
      });
    },
    '/departments': () => {
      queryClient.prefetchQuery({
        queryKey: queryKeys.departments(),
        queryFn: () => import('../services/api').then(m => 
          m.apiService.fetchDepartments()
        ),
        staleTime: staleTimeConfig.departments,
      });
    },
    '/tenants': () => {
      queryClient.prefetchQuery({
        queryKey: queryKeys.tenants({ page: 1, pageSize: 25 }),
        queryFn: () => import('../services/api').then(m => 
          m.apiService.fetchTenants({ page: 1, pageSize: 25 })
        ),
        staleTime: staleTimeConfig.tenants,
      });
    },
    '/inspections/checklists': () => {
      queryClient.prefetchQuery({
        queryKey: queryKeys.checklists({ page: 1, pageSize: 25 }),
        queryFn: () => import('../services/api').then(m => 
          m.apiService.fetchInspectionParameters({ page: 1, pageSize: 25 })
        ),
        staleTime: staleTimeConfig.checklists,
      });
    },
  };

  const prefetchFn = routeDataMap[route];
  if (prefetchFn) {
    prefetchFn();
  }
};

/**
 * Hook to prefetch data on link hover
 * 
 * Use this on navigation links to prefetch data when user hovers,
 * making navigation feel instant.
 * 
 * @param route - The route to prefetch data for
 * 
 * @example
 * ```tsx
 * const { onMouseEnter } = usePrefetchOnHover('/equipment');
 * 
 * return (
 *   <Link to="/equipment" onMouseEnter={onMouseEnter}>
 *     Equipment
 *   </Link>
 * );
 * ```
 */
export const usePrefetchOnHover = (route: string) => {
  const queryClient = useQueryClient();

  const onMouseEnter = () => {
    prefetchRouteData(route, queryClient);
  };

  return { onMouseEnter };
};

/**
 * Hook to manually prefetch specific data
 * 
 * Use this when you know the user will need specific data soon.
 * 
 * @example
 * ```tsx
 * const { prefetchEquipment, prefetchInspections } = useManualPrefetch();
 * 
 * // Prefetch when opening a modal or expanding a section
 * const handleOpenDetails = (equipmentId: string) => {
 *   prefetchInspections({ equipmentId });
 *   openModal();
 * };
 * ```
 */
export const useManualPrefetch = () => {
  const queryClient = useQueryClient();

  return {
    prefetchEquipment: (params?: Record<string, unknown>) => {
      queryClient.prefetchQuery({
        queryKey: queryKeys.equipment(params),
        queryFn: () => import('../services/api').then(m => 
          m.apiService.fetchEquipment(params)
        ),
        staleTime: staleTimeConfig.equipment,
      });
    },
    prefetchInspections: (params?: Record<string, unknown>) => {
      queryClient.prefetchQuery({
        queryKey: queryKeys.inspections(params),
        queryFn: () => import('../services/api').then(m => 
          m.apiService.fetchInspections(params)
        ),
        staleTime: staleTimeConfig.inspections,
      });
    },
    prefetchMaintenance: (params?: Record<string, unknown>) => {
      queryClient.prefetchQuery({
        queryKey: queryKeys.maintenance(params),
        queryFn: () => import('../services/api').then(m => 
          m.apiService.fetchMaintenanceTickets(params)
        ),
        staleTime: staleTimeConfig.maintenance,
      });
    },
    prefetchInventory: (params?: Record<string, unknown>) => {
      queryClient.prefetchQuery({
        queryKey: queryKeys.inventory(params),
        queryFn: () => import('../services/api').then(m => 
          m.apiService.fetchInventory(params)
        ),
        staleTime: staleTimeConfig.inventory,
      });
    },
  };
};
